/**
 * App motion kit (CC6) - the landing's Apple-style springs, shared by the
 * shell and every page.
 *
 * Rules (from the landing apple-design pass):
 * - Critically damped springs (no overshoot) for anything that just appears;
 *   motion is graceful, not bouncy, when the user supplied no momentum.
 * - Reveals are interruptible by construction: framer springs animate from
 *   the live value, unlike fixed cubic keyframes.
 * - Reduced motion is honoured app-wide through <MotionConfig
 *   reducedMotion="user"> in App.tsx: transforms and layout animations are
 *   dropped, opacity fades remain. Nothing here needs its own media query.
 * - Everything uses DIRECT object animations (initial/animate/whileInView
 *   objects), never variant-label propagation: label orchestration froze
 *   children at their initial pose in the landing subtree (the blank
 *   onboarding headline bug), while object animations behave. Staggering is
 *   done with explicit per-item delays, which reads the same.
 *
 * Exports:
 *   SPRING, SPRING_FAST      - transitions for layout/route/reveal and presses
 *   Reveal                   - fade-and-rise once when scrolled into view
 *   Rise                     - fade-and-rise on mount, staggered by `index`
 *   Pressable                - wrapper that scales to 0.98 while pressed
 *   pressable                - the same, as props to spread on a motion element
 *   PageEnter                - route-level fade plus 8px rise (used by PageTransition)
 */
import React from 'react';
import { motion, type Transition, type HTMLMotionProps } from 'framer-motion';

/** Critically damped default: no bounce, settles in about 0.55s. */
export const SPRING: Transition = { type: 'spring', bounce: 0, duration: 0.55 };

/** Snappier spring for small interactive elements (chips, buttons, pills). */
export const SPRING_FAST: Transition = { type: 'spring', bounce: 0, duration: 0.3 };

export const REVEAL_FROM = { opacity: 0, y: 24 } as const;
export const REVEAL_TO = { opacity: 1, y: 0 } as const;
export const VIEWPORT = { once: true, margin: '0px 0px -80px 0px' } as const;

/** Mount entrance: a shorter rise than the scroll reveal so stacked cards
 *  settle quickly on a page that is already on screen. */
export const RISE_FROM = { opacity: 0, y: 12 } as const;
export const RISE_TO = { opacity: 1, y: 0 } as const;

/** Route entrance: fade plus an 8px rise, no exit. */
export const PAGE_FROM = { opacity: 0, y: 8 } as const;
export const PAGE_TO = { opacity: 1, y: 0 } as const;

const TAGS = {
  div: motion.div,
  section: motion.section,
  article: motion.article,
  header: motion.header,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  p: motion.p,
  span: motion.span,
  ul: motion.ul,
  li: motion.li,
  a: motion.a,
  button: motion.button,
} as const;

export type MotionTag = keyof typeof TAGS;

/** Stagger step between siblings, in seconds. */
export const STAGGER = 0.06;

interface RevealProps extends React.HTMLAttributes<HTMLElement> {
  as?: MotionTag;
  /** Seconds - stagger siblings with e.g. index * STAGGER. */
  delay?: number;
  /** Framer hover lift (px). CSS hover translates are dead on motion
   *  elements (framer owns the inline transform), so lift lives here. */
  lift?: number;
  /** Opt-out for long lists (PP-02): renders a plain element with no motion
   *  value, observer or layer. Wrap the list's section in a Reveal instead. */
  plain?: boolean;
  children?: React.ReactNode;
  href?: string;
}

/**
 * Fade-and-rise into view, once, on scroll. Drop-in replacement for
 * data-aos="fade-up" (delay replaces data-aos-delay, in seconds).
 */
const HAS_INTERSECTION_OBSERVER = typeof IntersectionObserver !== 'undefined';

export const Reveal = ({
  as = 'div',
  delay = 0,
  lift,
  plain,
  children,
  style,
  ...rest
}: RevealProps): JSX.Element => {
  const Tag = TAGS[as] as any;
  // will-change promotes the element to its own layer for the reveal so the
  // per-frame transform/opacity writes composite instead of repainting the
  // card (and its shadow). PP-02: it is set only while the spring runs -
  // from the viewport-enter callback until settle - so an element still
  // below the fold holds no layer while it waits (the old version promoted
  // every unrevealed element from mount).
  const [phase, setPhase] = React.useState<'idle' | 'active' | 'settled'>(
    HAS_INTERSECTION_OBSERVER ? 'idle' : 'active',
  );
  // whileInView needs IntersectionObserver; where it is missing (jsdom, very
  // old engines) the element simply rises on mount instead of staying hidden.
  const inViewProps = HAS_INTERSECTION_OBSERVER
    ? {
        whileInView: REVEAL_TO,
        viewport: VIEWPORT,
        onViewportEnter: () => setPhase((p) => (p === 'idle' ? 'active' : p)),
      }
    : { animate: REVEAL_TO };
  if (plain) {
    const Plain = as as any;
    return (
      <Plain style={style} {...rest}>
        {children}
      </Plain>
    );
  }
  const promoted = phase === 'active' || !!lift;
  return (
    <Tag
      initial={REVEAL_FROM}
      {...inViewProps}
      transition={{ ...SPRING, delay }}
      onAnimationComplete={() => setPhase('settled')}
      whileHover={lift ? { y: -lift } : undefined}
      style={{ ...(style || {}), willChange: promoted ? 'transform, opacity' : undefined }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

interface RiseProps extends React.HTMLAttributes<HTMLElement> {
  as?: MotionTag;
  /** Position in the stagger; delay is index * STAGGER seconds. */
  index?: number;
  /** Extra delay in seconds, added to the stagger. */
  delay?: number;
  children?: React.ReactNode;
}

/**
 * Fade-and-rise on mount with a per-item stagger. Replaces the `.dash-reveal`
 * keyframes and `--reveal-i` counters: `<Rise index={2}>` reads the same, but
 * the spring is interruptible and never dips cached content back to opacity 0
 * on re-render (the animation only runs when the element mounts).
 */
export const Rise = ({
  as = 'div',
  index = 0,
  delay = 0,
  children,
  style,
  ...rest
}: RiseProps): JSX.Element => {
  const Tag = TAGS[as] as any;
  const [settled, setSettled] = React.useState(false);
  return (
    <Tag
      initial={RISE_FROM}
      animate={RISE_TO}
      transition={{ ...SPRING, delay: index * STAGGER + delay }}
      onAnimationComplete={() => setSettled(true)}
      style={{ ...(style || {}), willChange: settled ? undefined : 'transform, opacity' }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * Spread onto motion elements the user can press: an instant, spring-settled
 * press to 0.98. Hover feedback stays in CSS (opacity-only glow) so the
 * transform never fights it.
 */
export const pressable = {
  whileTap: { scale: 0.98 },
  transition: SPRING_FAST,
} as const;

export type PressableTag = 'div' | 'button' | 'a' | 'span' | 'li';

/** Props follow the rendered element: `as="button"` accepts `type`,
 *  `disabled` and the other button attributes; `as="a"` accepts `href`. */
export type PressableProps<T extends PressableTag = 'div'> = Omit<HTMLMotionProps<T>, 'as'> & {
  as?: T;
  children?: React.ReactNode;
};

/**
 * Wrapper that scales its content to 0.98 while pressed. Use `as="button"`
 * for a real button (typed as a button, so `type="button"` is accepted), or
 * wrap an existing card/link in the default div.
 */
export const Pressable = <T extends PressableTag = 'div'>({
  as,
  children,
  ...rest
}: PressableProps<T>): JSX.Element => {
  const Tag = TAGS[(as || 'div') as PressableTag] as any;
  return (
    <Tag whileTap={{ scale: 0.98 }} transition={SPRING_FAST} {...rest}>
      {children}
    </Tag>
  );
};

/**
 * Route-level entrance: opacity plus an 8px rise, keyed on the pathname by
 * PageTransition so every route change plays it once. No exit: an exiting
 * page would keep rendering against the new router location while it
 * faded, which is how the old inner fade got stuck at opacity 0.
 *
 * PP-02: this is a CSS keyframe (`.page-enter` in index.css), not a framer
 * spring. The route wrapper is the largest subtree on the page: framer
 * promoted it with will-change and wrote its transform from JS on every
 * frame, then re-rendered on settle. The keyframe is compositor-driven,
 * promoted only for its 0.42s, and leaves no transform, will-change or
 * containing block behind (fill-mode backwards only), so fixed sheets and
 * modals opened from the page are unaffected. Reduced motion drops it via
 * the global rule in index.css.
 */
export const PageEnter = ({
  children,
  className,
  style,
}: {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}): JSX.Element => (
  <div className={className ? `page-enter ${className}` : 'page-enter'} style={style}>
    {children}
  </div>
);
